import type { ActivatedRoute } from '@angular/router';
import { loadIntFilterFromGetParams, loadStringFilterFromGetParams } from '../reporting-core/utils';

export enum GiftProductType {
  membership = 'MEM',
  entryPass = 'ENP',
  voucherPack = 'VOP',
  giftCard = 'GCD',
}

export enum GiftStatus {
  unpaid = 'PAY',
  scheduled = 'SCH',
  delivered = 'DEL',
  redeemed = 'RED',
  expired = 'EXP',
  depleted = 'DPL',
}

export interface Gift {
  id: number;
  url: string;
  product_type: GiftProductType;
  product_id: number;
  product_name: string;
  status: GiftStatus,
  purchaser_id: number | null;
  purchaser_first_name: string | null;
  purchaser_last_name: string | null;
  purchaser_birthday: string | null;
  purchaser_email: string | null;
  recipient_name: string;
  recipient_email: string;
  redeemed_by_id: number | null;
  redeemed_by_first_name: string | null;
  redeemed_by_last_name: string | null;
  redeemed_by_birthday: string | null;
  redeemed_by_email: string | null;
  redeemed_for_id: number | null;
  redeemed_for_first_name: string | null;
  redeemed_for_last_name: string | null;
  redeemed_for_birthday: string | null;
  redeemed_for_email: string | null;
  redemption_code: string;
  banner_image_id: number | null;
  banner_image_name: string | null;
  deliver_at: string;
  expires_at: string | null;
  expiration_date: string | null;
  purchase_completed_at: string;
  gift_redeemed_at: string | null;
  location_id: number | null;
  location_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface GiftPrintBannerImageOption {
  id: number;
  url: string;
  name: string;
  image: string | null;
  created_at: string;
  updated_at: string;
}

export interface GiftCardBalanceState {
  state: 'loading' | 'loaded' | 'error';
  balance?: string;
}

export interface GiftFilters {
  product_type?: GiftProductType;
  status?: GiftStatus;
  purchase_completed_at_min?: string;
  purchase_completed_at_max?: string;
  purchase_completed_at_range?: string;
  location_id?: number;
}

export function loadGiftFiltersFromGetParams(activatedRoute: ActivatedRoute): GiftFilters {
  const filters: GiftFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadStringFilterFromGetParams(filters, 'product_type', initialQPs);
  loadStringFilterFromGetParams(filters, 'status', initialQPs);
  loadStringFilterFromGetParams(filters, 'purchase_completed_at_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'purchase_completed_at_max', initialQPs);
  loadStringFilterFromGetParams(filters, 'purchase_completed_at_range', initialQPs);
  loadIntFilterFromGetParams(filters, 'location_id', initialQPs);

  return filters;
}

export function getGiftProductTypeDisplay(productType: GiftProductType): string {
  switch (productType) {
    case GiftProductType.membership:
      return 'Membership';
    case GiftProductType.entryPass:
      return 'Entry Pass';
    case GiftProductType.voucherPack:
      return 'Voucher Pack';
    case GiftProductType.giftCard:
      return 'Gift Card';
    default:
      return 'Unknown';
  }
}

export function getGiftStatusDisplay(status: GiftStatus): string {
  switch (status) {
    case GiftStatus.unpaid:
      return 'Unpaid';
    case GiftStatus.scheduled:
      return 'Scheduled';
    case GiftStatus.delivered:
      return 'Delivered';
    case GiftStatus.redeemed:
      return 'Redeemed';
    case GiftStatus.expired:
      return 'Expired';
    case GiftStatus.depleted:
      return 'Depleted';
    default:
      return 'Unknown';
  }
}
